import { DeleteOutlined, DragOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Col, Input, Row, Tooltip, Typography } from 'antd'
import { clone, isNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { LocalFileFragment, useDeleteBannerMutation, useGetBannersQuery } from '@/generated/graphql'

import { useBannerImageUpload } from './banner-images-upload.hooks'
import s from './banner-images.module.less'

export interface BannerImageProps {
  bannerId: number
  index?: number | null | undefined
  link?: string | null | undefined
  file: LocalFileFragment
}

type DragSourceType = 'source' | 'target'

export const BannerImages: React.FC<{
  displays: {
    bannerId: number
    link: string
  }[]
  setDisplays: React.Dispatch<
    React.SetStateAction<
      {
        bannerId: number
        link: string
      }[]
    >
  >
}> = ({ displays, setDisplays }) => {
  const { t } = useTranslation()
  const displayCount = displays.length
  const { data, refetch } = useGetBannersQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  })

  const { uploadClickRef } = useBannerImageUpload()

  const [deleteBanner] = useDeleteBannerMutation({
    onCompleted() {
      refetch()
    },
  })

  React.useEffect(() => {
    if (data) {
      const images = data?.banners.edges.map(({ node }) => node)
      if (images.length > 0 && images.some((image) => !!image.index)) {
        const arr = displays.slice()
        images.forEach((image) => {
          if (isNumber(image.index)) {
            arr[image.index] = { bannerId: image.bannerId, link: image.link || '' }
          }
        })
        setDisplays(arr)
      }
    }
  }, [data])

  const images = data?.banners.edges.map(({ node }) => node) || []

  const orderDisplays = (index: number, bannerId: number, from: DragSourceType) => {
    if (from === 'target') {
      const arr = displays.slice()
      // 移除 target 中一个元素
      if (bannerId === 0) {
        arr[index] = { bannerId: 0, link: '' }
        setDisplays(arr)
        return
      }
      // 交换 target 中两个元素
      const findIndex = arr.findIndex((item) => item.bannerId === bannerId)
      arr[index] = clone(displays[findIndex])
      arr[findIndex] = clone(displays[index])
      setDisplays(arr)
      return
    } else {
      const banner = { bannerId, link: '' }
      // 添加元素
      if (displays[index].bannerId === 0) {
        const arr = displays.slice()
        arr[index] = banner
        setDisplays(arr)
        // 插入元素
      } else {
        const before = displays.slice(0, index)
        const after = displays.slice(index)
        const res = before.concat([banner]).concat(after).slice(0, displayCount)
        setDisplays(res)
      }
    }
  }

  const setLink = (link: string, index: number) => {
    const arr = displays.slice()
    arr[index] = { bannerId: displays[index].bannerId, link }
    setDisplays(arr)
  }

  const getImage = (id: number) => {
    return images.find((image) => image.bannerId === id)
  }

  const dragStart = (e: React.DragEvent<HTMLDivElement>, image: BannerImageProps, from: DragSourceType) => {
    e.dataTransfer.setData('id', image.bannerId + '')
    e.dataTransfer.setData('from', from)
  }

  const header = (title: string, actions?: React.ReactNode) => (
    <div className={s.header}>
      <div className={s.title}>
        <Typography.Title level={5}>{title}</Typography.Title>
      </div>
      <div className={s.actions}>{actions}</div>
    </div>
  )

  const imageDiv = (image: BannerImageProps, from: DragSourceType) => (
    <div className={s.imageContainer} draggable={true} onDragStart={(e) => dragStart(e, image, from)}>
      {from === 'source' && (
        <div className={s.deleteIcon}>
          <Button
            shape='circle'
            icon={<DeleteOutlined />}
            onClick={async (e) => {
              e.stopPropagation()
              await deleteBanner({
                variables: {
                  bannerId: image.bannerId,
                },
              })
            }}
          />
        </div>
      )}
      <img className={s.image} src={image.file.url} alt={image.file.name} />
    </div>
  )

  const sourceContent = (
    <div className={s.sourceImages}>
      <Row gutter={[16, 16]}>
        {images
          .filter((image) => !displays.some((display) => display.bannerId === image.bannerId))
          .map((image) => (
            <Col key={image.bannerId}>{imageDiv(image, 'source')}</Col>
          ))}
      </Row>
    </div>
  )

  const source = (
    <div className={s.source}>
      {header(
        'Banner 素材库，拖拽至右侧展示',
        <Tooltip title={t('banner.upload')}>
          <Button shape='circle' ref={uploadClickRef} icon={<UploadOutlined />} />
        </Tooltip>
      )}
      {sourceContent}
    </div>
  )

  const emptyDiv = (
    <div className={s.emptyBox}>
      <DragOutlined />
    </div>
  )

  const target = (
    <div className={s.target}>
      {header('Banner 展示，可拖拽改变展示顺序')}
      <Row className={s.row} gutter={[16, 16]}>
        {displays.map((display, index) => (
          <Col key={index}>
            <div
              draggable={!!displays[index].bannerId}
              onDragStart={(e) =>
                dragStart(e, images.find((image) => image.bannerId === displays[index].bannerId)!, 'target')
              }
              onDrop={(e) => {
                e.preventDefault()
                const bannerId = e.dataTransfer.getData('id')
                const from = e.dataTransfer.getData('from')
                orderDisplays(index, Number(bannerId), from as DragSourceType)
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className={s.targetBox}>
                <div className={s.actions}>
                  <span>{`No.${index + 1}`}</span>
                  <Button danger type='link' onClick={() => orderDisplays(index, 0, 'target')}>
                    移除图片
                  </Button>
                </div>
                {display.bannerId !== 0 && getImage(display.bannerId)
                  ? imageDiv(getImage(display.bannerId)!, 'target')
                  : emptyDiv}
                <Input
                  value={display.link}
                  disabled={display.bannerId === 0}
                  placeholder={t('banner.input')}
                  onChange={(e) => {
                    setLink(e.target.value ?? '', index)
                  }}
                />
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  )
  const container = (
    <div className={s.container}>
      {source}
      {target}
    </div>
  )

  return container
}
